import { type Action, createStore, createHook, createContainer } from 'react-sweet-state';
import type { JiraSuggestedChildIssueStatusType } from '@atlassian/jira-relay/src/__generated__/issueBreakdownSubscription.graphql';

export const steps = {
	defaultStep: 'defaultStep',
	promptInputStep: 'promptInputStep',
	draftListStep: 'draftListStep',
	errorStep: 'errorStep',
} as const;
export type Step = (typeof steps)[keyof typeof steps];

export const reasons = {
	accept: 'accept',
	reject: 'reject',
} as const;
type Reason = (typeof reasons)[keyof typeof reasons];

export const DEFAULT_SELECTED_ISSUE_INDEX = 0;

type ChildIssue = {
	summary: string;
	description: string;
};

export type SuggestedChildIssue = ChildIssue & {
	issueTypeId: string;
};

type State = {
	currentStep: Step;
	retryAttempts: number;
	suggestedChildIssues: SuggestedChildIssue[];
	promptValue: string;
	issueTypeIds: string[];
	headerPromptValue: string;
	selectedIssueIndex: number | null;
	channelId: string | null;
	acceptedChildIssues: ChildIssue[];
	rejectedChildIssues: ChildIssue[];
	isDiscardSuggestionsModalOpen: boolean;
	isStreaming: boolean;
	clearListWhenAddSuggestion: boolean;
	status: JiraSuggestedChildIssueStatusType | null;
};

const initialState: State = {
	currentStep: steps.defaultStep,
	retryAttempts: 0,
	suggestedChildIssues: [],
	promptValue: '',
	issueTypeIds: [],
	headerPromptValue: '',
	selectedIssueIndex: null,
	channelId: null,
	acceptedChildIssues: [],
	rejectedChildIssues: [],
	isDiscardSuggestionsModalOpen: false,
	isStreaming: false,
	clearListWhenAddSuggestion: false,
	status: null,
};

const setIssueBreakdownStep =
	(step: Step): Action<State> =>
	({ setState }) => {
		setState({ currentStep: step });
	};

const setSuggestedChildIssues =
	(suggestedChildIssues: SuggestedChildIssue[]): Action<State> =>
	({ setState }) => {
		setState({ suggestedChildIssues });
	};

const setSelectedIssueIndex =
	(selectedIssueIndex: number | null): Action<State> =>
	({ setState }) => {
		setState({ selectedIssueIndex });
	};

const unselectCurrentIndex =
	(): Action<State> =>
	({ setState }) => {
		setState({ selectedIssueIndex: null });
	};

const setChannelId =
	(channelId: string | null): Action<State> =>
	({ setState }) => {
		setState({ channelId });
	};

const resetIssueBreakdownState =
	(): Action<State> =>
	({ setState }) => {
		setState(initialState);
	};

const setPromptValue =
	(promptValue: string): Action<State> =>
	({ setState }) => {
		setState({ promptValue });
	};

const setIssueTypeIds =
	(issueTypeIds: string[]): Action<State> =>
	({ setState }) => {
		setState({ issueTypeIds });
	};

const setHeaderPromptValue =
	(headerPromptValue: string): Action<State> =>
	({ setState }) => {
		setState({ headerPromptValue });
	};
const openDiscardSuggestionsModal =
	(): Action<State> =>
	({ setState }) => {
		setState({ isDiscardSuggestionsModalOpen: true });
	};

const closeDiscardSuggestionsModal =
	(): Action<State> =>
	({ setState }) => {
		setState({ isDiscardSuggestionsModalOpen: false });
	};

const closeIssueBreakdown =
	(): Action<State> =>
	({ getState, setState }) => {
		const { currentStep } = getState();
		switch (currentStep) {
			case steps.draftListStep: // Go through discard modal
				setState({ isDiscardSuggestionsModalOpen: true });
				break;
			case steps.promptInputStep: // Reset the state
				setState(initialState);
				break;
			case steps.defaultStep: // Do nothing
			default:
		}
	};

const isIssueBreakdownOpen =
	(): Action<State, void, Boolean> =>
	({ getState }) => {
		const { currentStep } = getState();
		return currentStep !== steps.defaultStep;
	};

const getSuggestedLength =
	(): Action<State, void, number> =>
	({ getState }) => {
		const { suggestedChildIssues } = getState();
		return suggestedChildIssues.length;
	};

const updateSuggestedResult =
	({
		channelId,
		suggestedChildIssues,
		selectedIssueIndex = DEFAULT_SELECTED_ISSUE_INDEX,
	}: {
		channelId: string;
		suggestedChildIssues: SuggestedChildIssue[];
		selectedIssueIndex?: number | null;
	}): Action<State> =>
	({ setState }) => {
		setState({ channelId, suggestedChildIssues, selectedIssueIndex });
	};

const addSuggestedIssue =
	(newSuggestedIssue: SuggestedChildIssue): Action<State> =>
	({ getState, setState }) => {
		const { suggestedChildIssues: oldIssues, status, clearListWhenAddSuggestion } = getState();
		const suggestedChildIssues = clearListWhenAddSuggestion
			? [newSuggestedIssue]
			: oldIssues.concat(newSuggestedIssue);
		if (status) {
			setState({ suggestedChildIssues, status: null, clearListWhenAddSuggestion: false });
		} else {
			setState({ suggestedChildIssues, clearListWhenAddSuggestion: false });
		}
	};

const removeCurrentSuggestedChildIssue =
	(reason: Reason): Action<State> =>
	({ setState, getState }) => {
		const { selectedIssueIndex, suggestedChildIssues, acceptedChildIssues, rejectedChildIssues } =
			getState();
		if (selectedIssueIndex === null) return;
		const selectedIssue = suggestedChildIssues[selectedIssueIndex];
		const newState = {
			selectedIssueIndex: selectedIssueIndex % (suggestedChildIssues.length - 1),
			suggestedChildIssues: suggestedChildIssues.filter(
				(_item, index) => index !== selectedIssueIndex,
			),
		};
		switch (reason) {
			case reasons.accept:
				setState({
					...newState,
					acceptedChildIssues: acceptedChildIssues.concat({
						summary: selectedIssue.summary,
						description: selectedIssue.description,
					}),
				});
				break;
			case reasons.reject:
			default:
				setState({
					...newState,
					rejectedChildIssues: rejectedChildIssues.concat({
						summary: selectedIssue.summary,
						description: selectedIssue.description,
					}),
				});
				break;
		}
	};

const updateStreamingStatus =
	(isStreaming: boolean, status: State['status'] = null): Action<State> =>
	({ setState }) => {
		setState({ isStreaming, clearListWhenAddSuggestion: isStreaming, status });
	};

const setStreamingError =
	(): Action<State> =>
	({ setState, getState }) => {
		const { suggestedChildIssues, promptValue } = getState();
		if (suggestedChildIssues.length > 0 || promptValue) {
			// If it's a refine request,
			// for example, after the initial request has succeeded or the user has typed in text
			// then want to keep the channel ID.
			setState({ currentStep: steps.errorStep });
		} else {
			// If the initial request fails, we want to start a new one
			setState({ currentStep: steps.errorStep, channelId: null });
		}
	};

const incrementRetryCount =
	(): Action<State> =>
	({ getState, setState }) => {
		const { retryAttempts } = getState();
		setState({ retryAttempts: retryAttempts + 1 });
	};

export const actions = {
	setIssueBreakdownStep,
	setSuggestedChildIssues,
	setPromptValue,
	setIssueTypeIds,
	setHeaderPromptValue,
	setSelectedIssueIndex,
	unselectCurrentIndex,
	setChannelId,
	resetIssueBreakdownState,
	removeCurrentSuggestedChildIssue,
	updateSuggestedResult,
	addSuggestedIssue,
	openDiscardSuggestionsModal,
	closeDiscardSuggestionsModal,
	closeIssueBreakdown,
	updateStreamingStatus,
	setStreamingError,
	incrementRetryCount,
	isIssueBreakdownOpen,
	getSuggestedLength,
};

type ActionType = typeof actions;

export const IssueBreakdownAiContainer = createContainer();

const Store = createStore<State, ActionType>({
	initialState,
	actions,
	name: 'issue-breakdown',
	containedBy: IssueBreakdownAiContainer,
});

export const useIssueBreakdown = createHook(Store);
